import React, { useEffect, useRef, useState } from 'react';
import { 
  FaQuoteLeft, FaQuoteRight, FaGraduationCap, FaChalkboardTeacher,
  FaHeart, FaArrowRight, FaCalendarAlt, FaAward,
  FaUsers, FaBookOpen, FaLightbulb, FaHandshake,
  FaTrophy, FaGlobe, FaStar, FaRegClock
} from 'react-icons/fa';
import { MdOutlineAdminPanelSettings } from 'react-icons/md';

const PrincipalMessage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: <FaTrophy />, number: "25+", label: "Years of Excellence", color: "from-blue-500 to-blue-600" },
    { icon: <FaUsers />, number: "2000+", label: "Alumni Network", color: "from-blue-500 to-blue-600" },
    { icon: <FaGlobe />, number: "50+", label: "Placement Partners", color: "from-blue-500 to-blue-600" },
    { icon: <FaStar />, number: "100%", label: "Student Satisfaction", color: "from-blue-500 to-blue-600" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="bg-blue-600 p-3 rounded-2xl shadow-lg">
                <MdOutlineAdminPanelSettings className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-blue-700">Principal's Message</span>
            </h2>
            <div className="w-24 h-1 bg-blue-500 rounded-full mx-auto"></div>
          </div>

          {/* Single Beautiful Card */}
          <div className={`bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            
            {/* Top Gradient Bar */}
            <div className="h-2 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700"></div>
            
            {/* Main Content */}
            <div className="p-8 md:p-10">
              
              {/* Quote */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <FaQuoteLeft className="text-blue-400 text-3xl" />
                  <p className="text-gray-700 text-md italic leading-relaxed">
                    "Education is not just about acquiring knowledge; it's about transforming lives. 
                    At BNS Mahila Maha Vidyalaya, we are committed to empowering young women with 
                    quality education, strong values, and the confidence to achieve their dreams."
                  </p>
                  <FaQuoteRight className="text-blue-400 text-3xl self-end" />
                </div>
              </div>

              {/* Message Text */}
              <div className="mb-8">
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  <span className="text-blue-600 font-bold">Dear Students and Parents,</span>
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  It is my immense pleasure to welcome you to <span className="font-semibold text-blue-600">BNS Mahila Maha Vidyalaya</span>, 
                  an institution that stands as a beacon of <span className="font-semibold">women's empowerment through quality education</span>. 
                  Our commitment goes beyond academic excellence – we focus on building character, 
                  fostering leadership, and nurturing dreams.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  At BNS, we believe that every woman has the potential to create a lasting impact 
                  on society. Our comprehensive programs, experienced faculty, and state-of-the-art 
                  facilities provide the perfect platform for students to discover their strengths 
                  and pursue their passions.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  I invite you to be part of our vibrant community where learning knows no bounds, 
                  and every student is guided towards a bright and successful future. Together, 
                  let's write a story of empowerment, achievement, and transformation.
                </p>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-3 text-center hover:bg-blue-50 transition-all">
                  <FaTrophy className="text-blue-500 text-xl mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-800">25+</div>
                  <div className="text-gray-500 text-xs">Years Excellence</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center hover:bg-blue-50 transition-all">
                  <FaUsers className="text-blue-500 text-xl mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-800">2000+</div>
                  <div className="text-gray-500 text-xs">Alumni Network</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center hover:bg-blue-50 transition-all">
                  <FaGlobe className="text-blue-500 text-xl mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-800">50+</div>
                  <div className="text-gray-500 text-xs">Partners</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center hover:bg-blue-50 transition-all">
                  <FaStar className="text-blue-500 text-xl mx-auto mb-1" />
                  <div className="text-lg font-bold text-gray-800">100%</div>
                  <div className="text-gray-500 text-xs">Satisfaction</div>
                </div>
              </div>

              {/* Key Initiatives */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-3 text-center border border-blue-100">
                  <FaLightbulb className="text-blue-500 text-lg mx-auto mb-1" />
                  <p className="text-gray-700 text-xs font-semibold">Skill Development</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-3 text-center border border-blue-100">
                  <FaHandshake className="text-blue-500 text-lg mx-auto mb-1" />
                  <p className="text-gray-700 text-xs font-semibold">Industry Connect</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-3 text-center border border-blue-100">
                  <FaBookOpen className="text-blue-500 text-lg mx-auto mb-1" />
                  <p className="text-gray-700 text-xs font-semibold">Research Focus</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-3 text-center border border-blue-100">
                  <FaGlobe className="text-blue-500 text-lg mx-auto mb-1" />
                  <p className="text-gray-700 text-xs font-semibold">Global Exposure</p>
                </div>
              </div>

              {/* Signature and Contact */}
              <div className="flex flex-col md:flex-row justify-between items-center pt-4 border-t border-gray-100">
                <div className="text-center md:text-left mb-4 md:mb-0">
                  <div className="w-16 h-0.5 bg-blue-400 rounded-full mx-auto md:mx-0"></div>
                  <p className="text-blue-600 text-sm italic mt-2">"Empower. Educate. Excel."</p>
                </div>
                
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <FaRegClock className="text-blue-500 text-sm" />
                    <span className="text-gray-500 text-xs">Office Hours: 10:00 AM - 4:00 PM (Mon-Fri)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className={`text-center mt-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <button className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto shadow-md">
              Schedule a Meeting
              <FaArrowRight className="text-sm" />
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PrincipalMessage;